import { Dialog } from '@angular/cdk/dialog';
import { inject } from '@angular/core';

import type { CanDeactivateFn } from '@angular/router';

import {
  KtDsConfirmDialog,
  type KtDsConfirmDialogData,
} from '@shared/design-system/components/dialogs/kt-ds-confirm-dialog/kt-ds-confirm-dialog';

import { TranslocoService } from '@jsverse/transloco';

import { firstValueFrom } from 'rxjs';

import {
  ACTIVITY_EDIT_COVER_LEAVE_DIALOG_CANCEL_TEST_ID,
  ACTIVITY_EDIT_COVER_LEAVE_DIALOG_CONFIRM_TEST_ID,
  ACTIVITY_EDIT_COVER_LEAVE_DIALOG_TEST_ID,
  ACTIVITY_EDIT_STICKER_LEAVE_DIALOG_CANCEL_TEST_ID,
  ACTIVITY_EDIT_STICKER_LEAVE_DIALOG_CONFIRM_TEST_ID,
  ACTIVITY_EDIT_STICKER_LEAVE_DIALOG_TEST_ID,
} from '../activity-edit/constants/activity-edit-test-ids';
import type ActivityEditPage from '../activity-edit/pages/activity-edit-page/activity-edit-page';
import { ActivityCoverStore } from '../activity-edit/stores/activity-cover.store';
import { ActivityStickerStore } from '../activity-edit/stores/activity-sticker.store';
import { ACTIVITY_BROWSER_I18N_PREFIX } from '../kt.transloco';

const STICKER_LEAVE_DIALOG_KEY = `${ACTIVITY_BROWSER_I18N_PREFIX}.DETAIL.EDIT_PAGE.STICKER.LEAVE_DIALOG`;
const COVER_LEAVE_DIALOG_KEY = `${ACTIVITY_BROWSER_I18N_PREFIX}.DETAIL.EDIT_PAGE.COVER.LEAVE_DIALOG`;

const openLeaveQuestions = new WeakMap<object, Promise<boolean>>();

/**
 * Route-leave guard for the dedicated `activity/:id/edit` route.
 *
 * While a sticker generation runs, or a manual cover generation runs, it asks the teacher to confirm leaving.
 * No keeps the page and that generation (`false`). Yes aborts it and shows no error. Both questions are answered
 * before either abort, so declining the second one keeps the first run. An automatic cover already in the queue
 * is not asked about: leaving does not cancel it. Pages that eject themselves abort first, so a generation that
 * is no longer running asks nothing.
 *
 * Before the route is left it then flushes the fields that can hold a still-open in-progress edit (a valid edit
 * never blurred, e.g. on browser-back) through {@link ActivityEditPage.flushAllPendingEdits}:
 * name, description, steps, duration and sections are force-committed, then every in-flight autosave is awaited.
 *
 * Apart from the generation question this is a flush, NOT an unsaved-work confirmation: an invalid value never
 * reaches the store and the route may always be left. Wired in `kt.routes.ts`.
 *
 * @param component The edit page being left, used to flush its pending edits.
 * @returns `false` when the teacher chose to stay during a generation, otherwise `true` once the flush completes.
 */
export const activityEditCanDeactivateGuard: CanDeactivateFn<ActivityEditPage> = async (component) => {
  const stickerStore = inject(ActivityStickerStore);
  const coverStore = inject(ActivityCoverStore);
  const dialog = inject(Dialog);
  const transloco = inject(TranslocoService);

  const stickerRunning = stickerStore.generating();
  const coverManual = coverStore.manualActive();

  if (stickerRunning) {
    const messageKey = stickerStore.stickerUrl() === null ? 'MESSAGE_FIRST' : 'MESSAGE_REGENERATE';
    const confirmed = await askOnce(stickerStore, () =>
      askToLeaveDuringGeneration(dialog, {
        testId: ACTIVITY_EDIT_STICKER_LEAVE_DIALOG_TEST_ID,
        confirmTestId: ACTIVITY_EDIT_STICKER_LEAVE_DIALOG_CONFIRM_TEST_ID,
        cancelTestId: ACTIVITY_EDIT_STICKER_LEAVE_DIALOG_CANCEL_TEST_ID,
        title: transloco.translate(`${STICKER_LEAVE_DIALOG_KEY}.TITLE`),
        message: transloco.translate(`${STICKER_LEAVE_DIALOG_KEY}.${messageKey}`),
        acceptLabel: transloco.translate(`${STICKER_LEAVE_DIALOG_KEY}.YES`),
        rejectLabel: transloco.translate(`${STICKER_LEAVE_DIALOG_KEY}.NO`),
      }),
    );

    if (!confirmed) {
      return false;
    }
  }

  if (coverManual) {
    const messageKey = coverStore.view().cover === null ? 'MESSAGE_FIRST' : 'MESSAGE_REGENERATE';
    const confirmed = await askOnce(coverStore, () =>
      askToLeaveDuringGeneration(dialog, {
        testId: ACTIVITY_EDIT_COVER_LEAVE_DIALOG_TEST_ID,
        confirmTestId: ACTIVITY_EDIT_COVER_LEAVE_DIALOG_CONFIRM_TEST_ID,
        cancelTestId: ACTIVITY_EDIT_COVER_LEAVE_DIALOG_CANCEL_TEST_ID,
        title: transloco.translate(`${COVER_LEAVE_DIALOG_KEY}.TITLE`),
        message: transloco.translate(`${COVER_LEAVE_DIALOG_KEY}.${messageKey}`),
        acceptLabel: transloco.translate(`${COVER_LEAVE_DIALOG_KEY}.YES`),
        rejectLabel: transloco.translate(`${COVER_LEAVE_DIALOG_KEY}.NO`),
      }),
    );

    if (!confirmed) {
      return false;
    }
  }

  if (stickerRunning) {
    stickerStore.abort();
  }

  if (coverManual) {
    coverStore.dismissManualForLeave();
  }

  await component.flushAllPendingEdits();

  return true;
};

type LeavePrompt = {
  testId: string;
  confirmTestId: string;
  cancelTestId: string;
  title: string;
  message: string;
  acceptLabel: string;
  rejectLabel: string;
};

/**
 * A second navigation while the dialog is open runs the guard again. Sharing the first answer keeps one dialog on
 * screen, so a Yes can never abort a generation on behalf of a navigation the router already discarded.
 *
 * @param key The store whose generation the question is about.
 * @param ask Opens the dialog; only called when no question is open yet.
 * @returns The answer of the question that is open, or of the one just asked.
 */
function askOnce(key: object, ask: () => Promise<boolean>): Promise<boolean> {
  const open = openLeaveQuestions.get(key);

  if (open) {
    return open;
  }

  const question = ask().finally(() => openLeaveQuestions.delete(key));
  openLeaveQuestions.set(key, question);

  return question;
}

/**
 * Opens the warning confirm dialog for leaving during a generation.
 *
 * @param dialog Opens the dialog.
 * @param data Title, message, test ids and button labels.
 * @returns `true` only when the teacher confirmed; Escape, a backdrop click and No all count as staying.
 */
async function askToLeaveDuringGeneration(dialog: Dialog, data: LeavePrompt): Promise<boolean> {
  const ref = dialog.open<boolean, KtDsConfirmDialogData, KtDsConfirmDialog>(KtDsConfirmDialog, {
    data: {
      testId: data.testId,
      title: data.title,
      description: data.message,
      dialogVariant: 'warning',
      cancelButton: { testId: data.cancelTestId, label: data.rejectLabel },
      confirmButton: { testId: data.confirmTestId, label: data.acceptLabel },
    },
  });
  const closed = await firstValueFrom(ref.closed, { defaultValue: undefined });

  return closed === true;
}
